"use client";

import { googleSignIn, register } from "@/actions/auth";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { getMarketingPlanDisplayName } from "@/lib/marketing-plans";
import Link from "next/link";
import { useActionState } from "react";
import { PasswordInput } from "@/components/ui/PasswordInput";

export default function RegisterForm({ plan }: { plan: string }) {
  const [errorMessage, dispatch] = useActionState(register, undefined);
  const showGoogleAuth = true;
  const planLabel = getMarketingPlanDisplayName(plan);

  return (
    <div className="w-full max-w-md space-y-6 p-4 md:space-y-8">
      <div className="mb-2 flex justify-center lg:hidden">
        <Logo size="md" className="origin-center scale-90 md:scale-100" />
      </div>

      <div className="space-y-3 text-center">
        <h1 className="font-inter text-3xl font-black tracking-tight text-slate-900 dark:text-slate-100 md:text-4xl">
          Crear cuenta
        </h1>
        <p className="font-medium text-slate-500 dark:text-slate-400">
          Plan <span className="font-semibold text-[#1E3A8A] dark:text-blue-300">{planLabel}</span>{" "}
          seleccionado
        </p>
      </div>

      <div className="space-y-6">
        <form action={dispatch} className="space-y-5">
          <input type="hidden" name="plan" value={plan} />
          <Input
            label="Nombre completo"
            type="text"
            name="name"
            placeholder="Ej. Juan Perez"
            required
          />
          <Input
            label="Correo electrónico"
            type="email"
            name="email"
            placeholder="tu@email.com"
            required
          />
          <PasswordInput
            label="Contraseña"
            name="password"
            placeholder="Mínimo 8 caracteres"
            required
          />

          <div className="space-y-4">
            {/* Una casilla explícita en vez de la leyenda "al registrarte aceptas": sin firma
                manuscrita, lo que sostiene el acuerdo es poder mostrar un acto de aceptación
                deliberado. El servidor guarda quién, cuándo y qué versión del texto. */}
            <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
              <input
                type="checkbox"
                name="acceptTerms"
                required
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 accent-[#1E3A8A]"
              />
              <span className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
                He leído y acepto los{" "}
                <Link href="/terminos" className="font-semibold underline" target="_blank">
                  Términos y Condiciones
                </Link>{" "}
                y la{" "}
                <Link href="/privacidad" className="font-semibold underline" target="_blank">
                  Política de Privacidad
                </Link>
                .
              </span>
            </label>

            <Button
              className="h-12 w-full rounded-xl border-0 bg-[#1E3A8A] font-bold text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-900 active:scale-[0.98]"
              type="submit"
            >
              Registrarse ahora
              <i className="fas fa-arrow-right ml-2 text-xs" />
            </Button>
          </div>

          <div className="h-6" aria-live="polite" aria-atomic="true">
            {errorMessage && (
              <p className="flex animate-shake items-center justify-center gap-1.5 text-xs font-bold text-red-600 dark:text-red-400">
                <i className="fas fa-circle-exclamation" /> {errorMessage}
              </p>
            )}
          </div>
        </form>

        {showGoogleAuth && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs font-black uppercase tracking-widest">
                <span className="bg-white px-4 text-slate-400 dark:bg-slate-950 dark:text-slate-500">
                  o continua con
                </span>
              </div>
            </div>

            <form
              action={async () => {
                await googleSignIn(plan);
              }}
            >
              <Button
                className="flex h-12 w-full items-center justify-center gap-3 border border-slate-200 bg-white text-slate-900 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                type="submit"
                variant="outline"
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" className="h-4 w-4" />
                Continuar con Google
              </Button>
            </form>
          </>
        )}
      </div>

      <div className="text-center">
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Ya tienes una cuenta?{" "}
          <Link href="/login" className="font-extrabold text-[#1E3A8A] hover:underline dark:text-blue-300">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
